/**
 * Tab "Contract Detail" — rebuild 1:1 theo Access `frmProcurementMainSubFormDetailsP`
 * / `…DetailsNPE`:
 *
 *   ┌ Header (Card): 2 picker P/NPE + grid field 3 cột × 8 hàng (ContractDetailHeader) ┐
 *   ├ Nav buttons: P = [Pay Info]; NPE = [Pay Info][Advance][Fixation]                 ┤
 *   └ Body: PayInfo (3 grid GoodsAlc/Paydata/Invoice) | Advance | Fixation             ┘
 *
 * Chọn picker P → variant P (Access đổi SourceObject navProcurement); chọn picker NPE
 * → variant NPE. PERSIST theo user (skill /user-logaction): `variant` + contract_no
 * từng variant ở `user_preferences` scope `purchase` / key `contract_detail`.
 *
 * Contract Detail là màn chi tiết 1 hợp đồng được chọn (Access DefaultView=0) — KHÔNG
 * chịu global Delivery-Latest filter của header Procurement.
 *
 * Action wiring (Access parity):
 *   - Tab1 "Write data": P+Outright → AddPayOutrightDialog; NPE → PayDataFormDialog
 *     (Advanced Add); guard forced-close / "Please choose Outright Contract!".
 *   - Tab1 "Write data PTBF": P+Differential → AddPayPTBFDialog; NPE → snack
 *     "No write data PTBF for NPE contract!".
 *   - Tab1 Invoice "Write data": AddInvoiceDialog (guard forced-close cho P).
 *   - Tab2 New/Copy/Edit → PayDataFormDialog; Delete → guard BE; Print → Pay Confirm.
 *   - Tab3 (Fixation, NPE) Add Pay → PayDataFormDialog (prefill P contract). Access
 *     chỉ có btnAddPay Visible=True; New/Edit/Delete/Download/Print đều Visible=False.
 */
import { Box } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import { usePagePermission } from '../../hooks/usePagePermission'
import { usePageFilters } from '../../hooks/usePageFilters'
import { usePreference } from '../../hooks/usePreference'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  invalidateContractDetailLists,
  useDeleteContractAdvanceMutation,
  useGetContractHeaderNPEQuery,
  useGetContractHeaderPQuery,
  useGetContractPicksQuery,
  useLazyGetFixationPaySuggestQuery,
  type AdvanceRow,
  type FixationPaySuggest,
  type FixationRow,
} from '../../store/api/procurementApi'
import { getErrorMessage } from '@/utils/error'
import ContractDetailAddInvoiceDialog from './ContractDetailAddInvoiceDialog'
import ContractDetailAddPayOutrightDialog from './ContractDetailAddPayOutrightDialog'
import ContractDetailAddPayPTBFDialog from './ContractDetailAddPayPTBFDialog'
import ContractDetailAdvance from './ContractDetailAdvance'
import ContractDetailFixation from './ContractDetailFixation'
import ContractDetailHeader, { type ContractVariant } from './ContractDetailHeader'
import ContractDetailPayInfo from './ContractDetailPayInfo'
import PayDataConfirmPrintDialog from './PayDataConfirmPrintDialog'
import PayDataFormDialog from './PayDataFormDialog'

type DetailMode = 'payinfo' | 'advance' | 'fixation'

interface ContractDetailPref {
  variant: ContractVariant
  npeContractNo: string | null
  pContractNo: string | null
  mode: DetailMode
  reqFx: string
}

const NPE_MODES: Array<{ key: DetailMode; label: string }> = [
  { key: 'payinfo', label: 'Pay Info' },
  { key: 'advance', label: 'Advance' },
  { key: 'fixation', label: 'Fixation' },
]

interface PayDataDialogState {
  open: boolean
  mode: 'new' | 'edit' | 'copy'
  paydataId: number | null
  initialContractNo: string | null
  /** Access AdvancedAdd form (Tab Advance / Tab1 write-data NPE): khoá + prefill. */
  advanceAdd: boolean
  /** Access btnAddPay_Click (Tab3 Fixation): suggest số liệu lên form. */
  fixationSuggest?: FixationPaySuggest | null
}

interface ConfirmState {
  kind: 'advance'
  id: number
  message: string
}

export default function ProcurementContractDetailView() {
  const pref = usePreference<ContractDetailPref>('procurement', 'contract_detail')
  const snack = useSnackbar()
  const dispatch = useDispatch()
  const { canEdit } = usePagePermission()

  const [variant, setVariant] = useState<ContractVariant>('npe')
  const [pContractNo, setPContractNo] = useState<string | null>(null)
  const [npeContractNo, setNpeContractNo] = useState<string | null>(null)
  const [mode, setMode] = useState<DetailMode>('payinfo')
  const [reqFx, setReqFx] = useState('')

  // Hydrate 1 lần từ preference khi load Page.
  const hydrated = useRef(false)
  useEffect(() => {
    if (pref.isLoading || hydrated.current) return
    hydrated.current = true
    setVariant(pref.value.variant ?? 'npe')
    setPContractNo(pref.value.pContractNo ?? null)
    setNpeContractNo(pref.value.npeContractNo ?? null)
    setMode(pref.value.mode ?? 'payinfo')
    setReqFx(pref.value.reqFx ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pref.isLoading])

  // Debounced persist on change (delta-only patch).
  useEffect(() => {
    if (!hydrated.current) return
    const t = setTimeout(() => {
      void pref.patch({ variant, npeContractNo, pContractNo, mode, reqFx }).catch(() => undefined)
    }, 450)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, npeContractNo, pContractNo, mode, reqFx])

  const picks = useGetContractPicksQuery()
  // ChooseContract list lọc theo khoảng Delivery Latest (filter global của trang Procurement).
  const { filters: procFilters } = usePageFilters<{ startDate?: string; endDate?: string }>('procurement')
  const filteredPicks = useMemo(() => {
    const data = picks.data
    if (!data) return data
    const from = procFilters?.startDate
    const to = procFilters?.endDate
    if (!from && !to) return data
    // Guard: BE chưa trả latest_delivery (chưa restart) → không lọc để tránh dropdown rỗng.
    const hasField = data.p.some((it) => it.latest_delivery != null) || data.npe.some((it) => it.latest_delivery != null)
    if (!hasField) return data
    const inRange = (d: string | null | undefined) => {
      if (!d) return false
      const day = String(d).slice(0, 10)
      if (from && day < from) return false
      if (to && day > to) return false
      return true
    }
    return {
      p: data.p.filter((it) => inRange(it.latest_delivery)),
      npe: data.npe.filter((it) => inRange(it.latest_delivery)),
    }
  }, [picks.data, procFilters?.startDate, procFilters?.endDate])
  const headerP = useGetContractHeaderPQuery(
    { contractNo: pContractNo ?? '' },
    { skip: variant !== 'p' || !pContractNo },
  )
  const headerNPE = useGetContractHeaderNPEQuery(
    { contractNo: npeContractNo ?? '' },
    { skip: variant !== 'npe' || !npeContractNo },
  )

  const contractNo = variant === 'p' ? pContractNo : npeContractNo
  const isForcedClose = variant === 'p' && Number(headerP.data?.is_forced_close_int ?? 0) !== 0
  const contractType = String(headerP.data?.contract_type ?? '')

  // Contract Type + Contract List (liên động): mỗi loại GIỮ contract riêng của nó
  // (đổi Type qua lại vẫn nhớ contract đã chọn từng bên → persist theo user).
  const handleSelectType = (v: ContractVariant) => {
    setVariant(v)
    setMode('payinfo')
  }
  const handlePickP = (value: string | null) => {
    setPContractNo(value)
    setVariant('p')
    setMode('payinfo')
  }
  const handlePickNPE = (value: string | null) => {
    setNpeContractNo(value)
    setVariant('npe')
    setMode('payinfo')
  }

  const refreshLists = () => {
    dispatch(invalidateContractDetailLists())
  }

  // ---- PayDataFormDialog (Advanced Add / Add Pay / Tab2 New-Copy-Edit) ----
  const [payDataDialog, setPayDataDialog] = useState<PayDataDialogState>({
    open: false,
    mode: 'new',
    paydataId: null,
    initialContractNo: null,
    advanceAdd: false,
    fixationSuggest: null,
  })
  const closePayDataDialog = () => setPayDataDialog((p) => ({ ...p, open: false }))
  const [fetchFixationPaySuggest] = useLazyGetFixationPaySuggestQuery()

  // ---- Pay Confirm print (Tab2 Print) ----
  const [printPaydata, setPrintPaydata] = useState<{ id: number; xntt: string | null; paymentType: string | null; contractNo: string | null } | null>(null)

  // ---- Delete confirm (Advance) — Access NPE Fixation tab KHÔNG có Delete. ----
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)
  const [deleteAdvance, { isLoading: deletingAdvance }] = useDeleteContractAdvanceMutation()

  const runConfirmedDelete = async () => {
    if (!confirm) return
    try {
      await deleteAdvance({ paydataId: confirm.id }).unwrap()
      snack.success('Successfully Deleted')
      setConfirm(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
      setConfirm(null)
    }
  }

  // ---- Tab1 Write buttons (Access btnPayDataOutright / btnPayDataPTBF / btnInvoiceTLC) ----
  const [outrightDialog, setOutrightDialog] = useState<{ open: boolean; requestedFx: string }>({
    open: false,
    requestedFx: '',
  })

  const handleWriteOutright = (requestedFx: string) => {
    if (!contractNo) {
      snack.error('Please choose ContractNo!')
      return
    }
    if (variant === 'npe') {
      // Access: NPE → mở Advanced Add.
      setPayDataDialog({ open: true, mode: 'new', paydataId: null, initialContractNo: contractNo, advanceAdd: true })
      return
    }
    if (isForcedClose) {
      snack.error('This contract is forced close!')
      return
    }
    if (contractType !== 'Outright') {
      snack.error('Please choose Outright Contract!')
      return
    }
    setOutrightDialog({ open: true, requestedFx })
  }

  const [ptbfDialog, setPtbfDialog] = useState<{ open: boolean; requestedFx: string }>({
    open: false,
    requestedFx: '',
  })

  const handleWritePTBF = (requestedFx: string) => {
    if (!contractNo) {
      snack.error('Please choose ContractNo!')
      return
    }
    if (variant === 'npe') {
      snack.error('No write data PTBF for NPE contract!')
      return
    }
    if (isForcedClose) {
      snack.error('This contract is forced close!')
      return
    }
    if (contractType === 'Outright') {
      snack.error('Please choose Differential Contract!')
      return
    }
    setPtbfDialog({ open: true, requestedFx })
  }

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const handleWriteInvoice = () => {
    if (!contractNo) {
      snack.error('Please choose ContractNo!')
      return
    }
    if (isForcedClose) {
      snack.error('This contract is forced close!')
      return
    }
    setInvoiceDialogOpen(true)
  }
  const headerContractDate =
    variant === 'p'
      ? ((headerP.data?.contract_date as string | null) ?? null)
      : ((headerNPE.data?.contract_date as string | null) ?? null)

  // ---- Tab2 toolbar (Advance) ----
  const openAdvanceNew = () => {
    if (!contractNo) return
    setPayDataDialog({ open: true, mode: 'new', paydataId: null, initialContractNo: contractNo, advanceAdd: true })
  }
  const openAdvanceCopy = (row: AdvanceRow) =>
    setPayDataDialog({
      open: true,
      mode: 'copy',
      paydataId: row.paydata_p_id ?? null,
      initialContractNo: null,
      advanceAdd: true,
    })
  const openAdvanceEdit = (row: AdvanceRow) =>
    setPayDataDialog({
      open: true,
      mode: 'edit',
      paydataId: row.paydata_p_id ?? null,
      initialContractNo: null,
      advanceAdd: true,
    })
  const confirmDeleteAdvance = (row: AdvanceRow) => {
    if (row.paydata_p_id == null) return
    setConfirm({
      kind: 'advance',
      id: row.paydata_p_id,
      message: `Do you want to delete Advanced ID = '${row.paydata_p_id}'?`,
    })
  }
  const printAdvance = (row: AdvanceRow) => {
    if (row.paydata_p_id == null) return
    if (!String(row.xntt_no ?? '').trim()) {
      snack.error('XNTT No is Null!')
      return
    }
    setPrintPaydata({ id: row.paydata_p_id, xntt: row.xntt_no ?? null, paymentType: row.payment_type, contractNo: row.contract_no })
  }

  // ---- Tab3 toolbar (Fixation) — Access chỉ có btnAddPay (các nút khác Visible=False). ----
  // Access btnAddPay_Click: mở PayData form (new) + SUGGEST số liệu theo p_contract_no.
  const addPayFromFixation = async (row: FixationRow) => {
    const pcn = String(row.p_contract_no ?? '').trim()
    if (!pcn) {
      snack.error('This fixation has no P contract.')
      return
    }
    let fixationSuggest: FixationPaySuggest | null = null
    try {
      fixationSuggest = await fetchFixationPaySuggest({ pContractNo: pcn }).unwrap()
    } catch {
      /* suggest lỗi → vẫn mở form (cascade theo contract vẫn chạy) */
    }
    setPayDataDialog({ open: true, mode: 'new', paydataId: null, initialContractNo: pcn, advanceAdd: false, fixationSuggest })
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <ContractDetailHeader
        variant={variant}
        picks={filteredPicks}
        picksLoading={picks.isLoading || picks.isFetching}
        pContractNo={pContractNo}
        npeContractNo={npeContractNo}
        onPickP={handlePickP}
        onPickNPE={handlePickNPE}
        onSelectType={handleSelectType}
        mode={mode}
        modeOptions={(variant === 'npe' ? NPE_MODES : NPE_MODES.slice(0, 1)).map((m) => ({
          value: m.key,
          label: m.label,
        }))}
        onModeChange={(m) => setMode(m as DetailMode)}
        headerP={variant === 'p' ? headerP.data ?? null : null}
        headerNPE={variant === 'npe' ? headerNPE.data ?? null : null}
      />

      {!contractNo ? (
        <BrandState
          tone="empty"
          title="Select a contract"
          description="Pick a P Contract or NPE Contract above to view its details."
        />
      ) : mode === 'advance' && variant === 'npe' ? (
        <ContractDetailAdvance
          contractNo={contractNo}
          canEdit={canEdit && !isForcedClose}
          onNew={openAdvanceNew}
          onCopy={openAdvanceCopy}
          onEdit={openAdvanceEdit}
          onDelete={confirmDeleteAdvance}
          onPrint={printAdvance}
        />
      ) : mode === 'fixation' && variant === 'npe' ? (
        <ContractDetailFixation
          contractNo={contractNo}
          canEdit={canEdit && !isForcedClose}
          onAddPay={addPayFromFixation}
        />
      ) : (
        <ContractDetailPayInfo
          variant={variant}
          contractNo={contractNo}
          contractType={contractType}
          fixLevel={variant === 'p' ? headerP.data?.fix_level ?? null : null}
          fixedFx={variant === 'p' ? headerP.data?.fixed_fx ?? null : null}
          reqFx={reqFx}
          onReqFxChange={setReqFx}
          onWriteOutright={handleWriteOutright}
          onWritePTBF={handleWritePTBF}
          onWriteInvoice={handleWriteInvoice}
          disabled={isForcedClose}
        />
      )}

      {/* ---- Dialogs ---- */}
      {contractNo ? (
        <ContractDetailAddInvoiceDialog
          open={invoiceDialogOpen}
          contractNo={contractNo}
          contractDate={headerContractDate}
          onClose={() => setInvoiceDialogOpen(false)}
          onSaved={refreshLists}
        />
      ) : null}

      {contractNo ? (
        <ContractDetailAddPayOutrightDialog
          open={outrightDialog.open}
          contractNo={contractNo}
          requestedFx={outrightDialog.requestedFx}
          onClose={() => setOutrightDialog((p) => ({ ...p, open: false }))}
          onSaved={refreshLists}
        />
      ) : null}

      {contractNo ? (
        <ContractDetailAddPayPTBFDialog
          open={ptbfDialog.open}
          contractNo={contractNo}
          requestedFx={ptbfDialog.requestedFx}
          fixLevel={variant === 'p' ? headerP.data?.fix_level ?? null : null}
          onClose={() => setPtbfDialog((p) => ({ ...p, open: false }))}
          onSaved={refreshLists}
        />
      ) : null}

      <PayDataFormDialog
        open={payDataDialog.open}
        mode={payDataDialog.mode}
        paydataId={payDataDialog.paydataId}
        canEdit={canEdit}
        initialContractNo={payDataDialog.initialContractNo}
        advanceAdd={payDataDialog.advanceAdd}
        fixationSuggest={payDataDialog.fixationSuggest ?? null}
        onClose={closePayDataDialog}
        onSaved={() => {
          closePayDataDialog()
          refreshLists()
        }}
      />

      <PayDataConfirmPrintDialog
        open={printPaydata !== null}
        paydataId={printPaydata?.id ?? null}
        xnttNo={printPaydata?.xntt ?? null}
        paymentType={printPaydata?.paymentType ?? null}
        contractNo={printPaydata?.contractNo ?? null}
        onClose={() => setPrintPaydata(null)}
      />

      <ConfirmDialog
        open={confirm !== null}
        title="Delete Advance"
        message={confirm?.message ?? ''}
        danger
        loading={deletingAdvance}
        onConfirm={() => void runConfirmedDelete()}
        onClose={() => setConfirm(null)}
      />
    </Box>
  )
}
